import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Input } from "antd";
import {
  CloseOutlined,
  InsertRowBelowOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import classnames from "classnames";
import QuickAddStagingGrid from "./QuickAddStagingGrid";
import { useDebounce } from "use-debounce";
import { pricerWorkingDate } from "helpers/utcCalendarDate";
import FloatingPanel from "components/Common/FloatingPanel/FloatingPanel";
import { PrimaryButton } from "components/Common/CustomButtons/CustomButtons";
import { PricerContext } from "contexts/PricerContext";
import { copyToClipboard, toast } from "helpers/generalHelper";
import {
  ACTIONS,
  CATEGORIES,
  COMPONENT_NAMES,
  CONTEXTS,
  EVENT_NAMES,
  sendEvent,
} from "helpers/googleAnalyticsHelper";
import { toastImportValidationResponse } from "helpers/importCellErrors";
import { isErrorResponse } from "helpers/tradeCaptureHelper";
import QuickAddHelp from "./QuickAddHelp";
import { QuickAddTeamRunPopup } from "./QuickAddTeamRunPopup";
import { triggerPushOptionToTeamRunAPI } from "api/teamRunApi";
import { teamRunPopupFromPushCheck } from "helpers/QuickAdd/quickAddTeamRunPush";
import {
  discardQuickAddDrafts,
  duplicateQuickAddDraft,
  fetchQuickAddCopyText,
  fetchQuickAddDrafts,
  finishQuickAddTeamRunPush,
  logQuickAddShorthand,
  stageQuickAddDrafts,
  submitQuickAddDraftsToPricer,
} from "./quickAddApi";
import { isTenorExpired, parseLine } from "./optionShorthandParser";
import { getSuggestion } from "./quickAddSuggestion";
import { getRankings, recordUsage } from "./quickAddUsage";
import { toCsvRow, toQuickAddApiRow } from "./quickAddToCsvRow";
import useShorthandGrammar from "./useShorthandGrammar";
import "./QuickAdd.scss";

/**
 * Add stages each line as a real option on the server (hidden from the
 * Pricer, seeded from EST, typed premium/vol applied like a Pricer summary
 * edit). The grid shows the Pricer columns through Ask Qty, then Average, Cost
 * of Carry and the Black-76 inputs, and every edit reprices through the Pricer
 * endpoints. Submit to Pricer moves the options into the Pricer; Push to Team
 * Run runs the Pricer push-check popup per option and never adds it to the
 * Pricer. Both copy the no vol/prem shorthands and remove what they sent.
 */
const PANEL_OPTIONS = {
  widthRatio: 0.88,
  heightRatio: 0.62,
  minWidth: 960,
  minHeight: 420,
  margin: 16,
  storageKey: "quick-add-panel-bounds",
};

const HISTORY_MAX = 50;

/** Panel z-index while a Team Run popup is open: just below antd Modal (1000). */
const QUICK_ADD_PANEL_Z_BEHIND_MODAL = 990;

/**
 * Annotated example shown above the input — teaches the format at a glance.
 * "Structure" is spelled out as strikes + option type, which is what trips
 * people up most.
 */
const SHORTHAND_ANATOMY = [
  { token: "EUA", label: "contract" },
  { token: "jun27", label: "tenor" },
  { token: "60/55", label: "strikes" },
  { token: "cs", label: "option type" },
  { token: "x56", label: "underlying" },
];

/** Short labels for the quiet live hint (one at a time). */
const LIVE_HINT_LABELS = {
  contract: "Need contract",
  tenor: "Need tenor",
  structure: "Need strikes + option type",
  underlying: "Need underlying (x56 or live)",
};

/**
 * Quiet next-step hint while typing. Rules (anti-clutter):
 * - Never toast; never a red box (Enter warning owns that).
 * - Only after something is recognizable, or the user finished a token (space).
 * - One item only — the first missing in contract → tenor → structure → underlying.
 * - Hidden when the line is already stageable.
 */
const getLiveMissingHint = (parseResult, debouncedInput) => {
  if (!parseResult || parseResult.ok) return "";
  const raw = String(debouncedInput || "").trim();
  if (!raw) return "";

  const missing = (parseResult.missing || []).filter(
    (m) => m !== "empty" && m !== "grammar"
  );
  if (!missing.length) return "";

  const hasProgress =
    !!parseResult.contract ||
    !!parseResult.tenors?.length ||
    !!parseResult.label ||
    !!parseResult.strikes?.length ||
    parseResult.live != null ||
    /\s/.test(String(debouncedInput || "").trimEnd());
  if (!hasProgress) return "";

  const order = ["contract", "tenor", "structure", "underlying"];
  const first = order.find((k) => missing.includes(k)) || missing[0];
  return LIVE_HINT_LABELS[first] || `Need ${first}`;
};

/** Human labels for parse `missing` keys (Enter / Add gate). */
const MISSING_LABELS = {
  empty: "a shorthand",
  grammar: "contract list (still loading)",
  contract: "contract (e.g. EUA)",
  tenor: "tenor (e.g. jun27)",
  structure: "strikes + option type (e.g. 60/55cs, 123/23 call spread)",
  underlying: "underlying (x56 or live)",
};

const formatMissingWarning = (missing = []) => {
  if (!missing.length || missing.includes("empty")) {
    return "Can't add yet — type contract, tenor, strikes + option type, and underlying (e.g. EUA jun27 60/55cs x56)";
  }
  const parts = missing.map((m) => MISSING_LABELS[m] || m);
  return `Can't add yet — missing: ${parts.join(" · ")}`;
};

const QuickAdd = ({ isOpen, onClose, refetchOptionRow, focusKey = 0 }) => {
  const {
    state: { dateSelected },
  } = useContext(PricerContext);

  const {
    data: grammarPack,
    isLoading: grammarLoading,
    isError: grammarError,
  } = useShorthandGrammar(isOpen);

  const [inputValue, setInputValue] = useState("");
  const [debouncedInput] = useDebounce(inputValue, 120);
  // Staged options: Pricer-format rows from the drafts API, plus the
  // Additional Info inputs and typed shorthand per summary_id.
  const [draftRows, setDraftRows] = useState([]);
  const [draftInputs, setDraftInputs] = useState({});
  const [draftShorthands, setDraftShorthands] = useState({});
  const [newSummaryId, setNewSummaryId] = useState(null);
  const [isStaging, setIsStaging] = useState(false);
  const [isStoring, setIsStoring] = useState(false);
  const [shake, setShake] = useState(false);
  const [stageWarning, setStageWarning] = useState("");
  const [rowLevelWarnings, setRowLevelWarnings] = useState([]);
  const [helpOpen, setHelpOpen] = useState(false);
  // Ghost suggestion: dismissed for one exact input value; tick refreshes
  // rankings after each staged line so new templates become suggestible.
  const [dismissedFor, setDismissedFor] = useState(null);
  const [usageTick, setUsageTick] = useState(0);
  const [inputOverflow, setInputOverflow] = useState(false);
  const inputRef = useRef(null);
  const teamRunQueueRef = useRef([]);
  const [teamRunPopup, setTeamRunPopup] = useState(null);
  // Summary ids edited in the grid since staging (store study log).
  const editedIdsRef = useRef(new Set());
  const timersRef = useRef([]);
  // ↑/↓ recall of previously staged lines. -1 = editing the live draft.
  const historyRef = useRef([]);
  const historyIdxRef = useRef(-1);
  const draftRef = useRef("");

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const schedule = useCallback((fn, ms) => {
    const id = setTimeout(fn, ms);
    timersRef.current.push(id);
    return id;
  }, []);

  const focusInput = useCallback(() => {
    const node = inputRef.current;
    if (!node) return;
    if (typeof node.focus === "function") node.focus();
    else if (node.input && typeof node.input.focus === "function") {
      node.input.focus();
    }
  }, []);

  const calculationDate = useMemo(
    () => pricerWorkingDate(dateSelected),
    [dateSelected]
  );

  // Option order = first appearance of each summary in the rows (staging order).
  const draftIds = useMemo(() => {
    const seen = new Set();
    (draftRows || []).forEach((row) => {
      if (row?.summary_id != null) seen.add(row.summary_id);
    });
    return [...seen];
  }, [draftRows]);
  const draftIdsRef = useRef(draftIds);
  draftIdsRef.current = draftIds;

  const draftProductIds = useMemo(() => {
    const map = new Map();
    (draftRows || []).forEach((row) => {
      if (row?.summary_id != null && !map.has(row.summary_id)) {
        map.set(row.summary_id, row.product_id ?? null);
      }
    });
    return map;
  }, [draftRows]);

  useEffect(() => {
    if (isOpen) {
      schedule(focusInput, 50);
    } else {
      // Staged options stay on the server and come back on the next open.
      clearTimers();
      setInputValue("");
      setNewSummaryId(null);
      setShake(false);
      setStageWarning("");
      setRowLevelWarnings([]);
      historyIdxRef.current = -1;
      draftRef.current = "";
    }
    return clearTimers;
  }, [isOpen, schedule, focusInput, clearTimers]);

  // Parent bumps focusKey when "/" is pressed while already open.
  useEffect(() => {
    if (isOpen && focusKey > 0) {
      schedule(focusInput, 0);
    }
  }, [focusKey, isOpen, schedule, focusInput]);

  const parseResult = useMemo(() => {
    if (!grammarPack || !debouncedInput.trim()) return null;
    return parseLine(debouncedInput, grammarPack);
  }, [debouncedInput, grammarPack]);

  const suggestion = useMemo(() => {
    if (!grammarPack || !inputValue.trim() || inputValue === dismissedFor) {
      return null;
    }
    return getSuggestion(inputValue, grammarPack, getRankings());
    // usageTick re-reads rankings after staging.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, grammarPack, dismissedFor, usageTick]);
  const suggestionRef = useRef(null);
  suggestionRef.current = suggestion;

  // Ghost alignment breaks once the input scrolls horizontally — hide it then.
  useEffect(() => {
    const el = inputRef.current?.input;
    if (!el) return;
    setInputOverflow(el.scrollWidth > el.clientWidth);
  }, [inputValue]);

  const pushHistory = useCallback((line) => {
    const trimmed = String(line || "").trim();
    if (!trimmed) return;
    const prev = historyRef.current;
    if (prev[0] !== trimmed) {
      historyRef.current = [trimmed, ...prev].slice(0, HISTORY_MAX);
    }
    historyIdxRef.current = -1;
    draftRef.current = "";
  }, []);

  const applyDraftResponse = useCallback((response) => {
    const data = response?.data?.data;
    if (!data || !Array.isArray(data.rows)) return false;
    setDraftRows(data.rows);
    setDraftInputs(data.inputs || {});
    setDraftShorthands(data.shorthands || {});
    return true;
  }, []);

  const reloadDrafts = useCallback(async () => {
    const response = await fetchQuickAddDrafts();
    if (isErrorResponse(response)) return;
    applyDraftResponse(response);
  }, [applyDraftResponse]);

  useEffect(() => {
    if (isOpen) reloadDrafts();
  }, [isOpen, reloadDrafts]);

  /**
   * POST parsed lines to the drafts API. Rejected lines get a row warning and
   * a study log; accepted ones are seeded and priced on the server.
   */
  const stageLines = useCallback(
    async (lines) => {
      const response = await stageQuickAddDrafts({
        table_data: lines.map((l, i) =>
          toQuickAddApiRow({ ...l.built.row, option: i + 1 })
        ),
        shorthands: lines.map((l) => l.shorthand),
        calculation_date: calculationDate,
      });

      if (isErrorResponse(response)) {
        const message =
          String(response?.data?.message ?? "").trim() ||
          "Couldn't add this option";
        lines.forEach((l) =>
          logQuickAddShorthand(l.shorthand, {
            validationMessage: message,
            rowEdited: false,
          })
        );
        const rows = toastImportValidationResponse(response);
        const rowMessages = rows.flatMap((r) => r.rowLevelMessages || []);
        setRowLevelWarnings(
          (rowMessages.length ? rowMessages : [message]).map(
            (m) => `${lines[0]?.shorthand || "Option"}: ${m}`
          )
        );
        return false;
      }

      applyDraftResponse(response);
      lines.forEach((l) => logQuickAddShorthand(l.shorthand));
      const warnings = response?.data?.data?.warnings || [];
      setRowLevelWarnings(warnings);
      if (warnings.length) toast(warnings.join("\n"), "warning");
      const staged = response?.data?.data?.staged_summary_ids || [];
      if (staged.length) {
        setNewSummaryId(staged[staged.length - 1]);
        schedule(() => setNewSummaryId(null), 1200);
      }
      return true;
    },
    [applyDraftResponse, calculationDate, schedule]
  );

  /** Enter and Add share this — stage only when parse is complete. */
  const handleStage = useCallback(async () => {
    const shorthand = String(inputValue ?? "").trim();
    if (!grammarPack) {
      const msg = "Can't add yet — still loading contracts and strategies";
      logQuickAddShorthand(shorthand, {
        validationMessage: msg,
        rowEdited: false,
      });
      setStageWarning(msg);
      toast(msg, "warning");
      return;
    }
    const parsed = parseLine(inputValue, grammarPack);
    if (!parsed.ok) {
      const msg = formatMissingWarning(parsed.missing);
      logQuickAddShorthand(shorthand, {
        validationMessage: msg,
        rowEdited: false,
      });
      setStageWarning(msg);
      setShake(true);
      schedule(() => setShake(false), 400);
      toast(msg, "warning");
      return;
    }
    if (isStaging) return;
    setStageWarning("");
    setRowLevelWarnings([]);
    setIsStaging(true);
    try {
      const ok = await stageLines([{ shorthand, built: toCsvRow(parsed) }]);
      if (!ok) {
        setShake(true);
        schedule(() => setShake(false), 400);
        return;
      }
    } catch {
      toast("Couldn't add the option. Please try again.", "error");
      return;
    } finally {
      setIsStaging(false);
    }
    pushHistory(inputValue);
    recordUsage(parsed, inputValue);
    setUsageTick((t) => t + 1);
    setInputValue("");
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER,
      action: ACTIONS.CLICK,
      component: COMPONENT_NAMES.BUTTON,
      eventName: EVENT_NAMES.PRICER_QUICK_ADD_ADD,
      extraParams: {
        row_count: 1,
      },
    });
    schedule(focusInput, 0);
  }, [
    grammarPack,
    inputValue,
    isStaging,
    schedule,
    focusInput,
    pushHistory,
    stageLines,
  ]);

  /** Multi-line paste: stage every parseable line, leave failures for review. */
  const handlePaste = useCallback(
    async (e) => {
      const text = e.clipboardData?.getData("text") || "";
      if (!text.includes("\n")) return; // single line → default paste
      e.preventDefault();
      const lines = text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      if (!grammarPack) {
        const msg = "Still loading contracts and strategies";
        toast(msg, "warning");
        lines.forEach((line) => {
          logQuickAddShorthand(line, {
            validationMessage: msg,
            rowEdited: false,
          });
        });
        return;
      }
      const failed = [];
      let stagedCount = 0;
      setIsStaging(true);
      setRowLevelWarnings([]);
      try {
        // One request per line so a bad line never blocks the others.
        for (const line of lines) {
          const parsed = parseLine(line, grammarPack);
          if (!parsed.ok) {
            logQuickAddShorthand(line, {
              validationMessage: formatMissingWarning(parsed.missing),
              rowEdited: false,
            });
            failed.push(line);
            continue;
          }
          // eslint-disable-next-line no-await-in-loop
          const ok = await stageLines([
            { shorthand: line, built: toCsvRow(parsed) },
          ]);
          if (!ok) {
            failed.push(line);
            continue;
          }
          stagedCount += 1;
          pushHistory(line);
          recordUsage(parsed, line);
        }
      } catch {
        toast("Couldn't add every line. Please try again.", "error");
      } finally {
        setIsStaging(false);
      }
      if (stagedCount) {
        setUsageTick((t) => t + 1);
        sendEvent({
          category: CATEGORIES.PRICER,
          context: CONTEXTS.PRICER,
          action: ACTIONS.CLICK,
          component: COMPONENT_NAMES.BUTTON,
          eventName: EVENT_NAMES.PRICER_QUICK_ADD_ADD,
          extraParams: {
            row_count: stagedCount,
          },
        });
      }
      if (failed.length) {
        // First failed line goes to the input to fix; the rest are on ↑.
        failed
          .slice(1)
          .reverse()
          .forEach((line) => pushHistory(line));
        setInputValue(failed[0]);
        toast(
          `Staged ${stagedCount} of ${lines.length} — ${failed.length} line${
            failed.length === 1 ? "" : "s"
          } need fixing (first is in the input${
            failed.length > 1 ? ", others on ↑" : ""
          })`,
          "warning"
        );
      } else {
        setInputValue("");
        toast(
          `Staged ${stagedCount} option${stagedCount === 1 ? "" : "s"} from paste`,
          "success"
        );
      }
      schedule(focusInput, 0);
    },
    [grammarPack, schedule, focusInput, pushHistory, stageLines]
  );

  /** Tab accepts the ghost, Esc dismisses it, ↑/↓ walk staged-line history. */
  const handleInputKeyDown = useCallback(
    (e) => {
      if (e.key === "Tab" && !e.shiftKey && suggestionRef.current) {
        e.preventDefault();
        setInputValue(suggestionRef.current.full);
        return;
      }
      if (e.key === "Escape" && suggestionRef.current) {
        setDismissedFor(e.target.value);
        return;
      }
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
      const history = historyRef.current;
      if (!history.length) return;
      e.preventDefault();
      let idx = historyIdxRef.current;
      if (e.key === "ArrowUp") {
        if (idx === -1) draftRef.current = e.target.value;
        idx = Math.min(idx + 1, history.length - 1);
        historyIdxRef.current = idx;
        setInputValue(history[idx]);
      } else {
        if (idx === -1) return;
        idx -= 1;
        historyIdxRef.current = idx;
        setInputValue(idx === -1 ? draftRef.current : history[idx]);
      }
    },
    []
  );

  const handleDeleteDraft = useCallback(
    async (summaryId) => {
      const response = await discardQuickAddDrafts([summaryId]);
      if (isErrorResponse(response)) {
        toastImportValidationResponse(response);
        return;
      }
      editedIdsRef.current.delete(summaryId);
      applyDraftResponse(response);
    },
    [applyDraftResponse]
  );

  const handleUndoLast = useCallback(() => {
    const ids = draftIdsRef.current;
    if (ids.length) handleDeleteDraft(ids[ids.length - 1]);
  }, [handleDeleteDraft]);

  const handleDuplicateDraft = useCallback(
    async (summaryId) => {
      const shorthand = draftShorthands?.[summaryId];
      if (shorthand) logQuickAddShorthand(shorthand);
      const response = await duplicateQuickAddDraft(summaryId);
      if (isErrorResponse(response)) {
        toastImportValidationResponse(response);
        return;
      }
      applyDraftResponse(response);
      const copy = response?.data?.data?.staged_summary_ids?.[0];
      if (copy) {
        setNewSummaryId(copy);
        schedule(() => setNewSummaryId(null), 1200);
      }
    },
    [applyDraftResponse, draftShorthands, schedule]
  );

  /** Grid edits mark the option as edited for the store study log. */
  const setDraftRowsFromGrid = useCallback((next) => {
    setDraftRows((prev) => {
      const nextRows = typeof next === "function" ? next(prev) : next;
      const prevById = new Map((prev || []).map((r) => [r.id, r]));
      (nextRows || []).forEach((row) => {
        const before = prevById.get(row.id);
        if (before && before !== row) editedIdsRef.current.add(row.summary_id);
      });
      return nextRows;
    });
  }, []);

  const logStoreSuccess = useCallback(
    (summaryIds) => {
      summaryIds.forEach((id) => {
        const shorthand = draftShorthands?.[id];
        if (!shorthand) return;
        logQuickAddShorthand(shorthand, {
          rowEdited: editedIdsRef.current.has(id),
        });
        editedIdsRef.current.delete(id);
      });
    },
    [draftShorthands]
  );

  const copyNoVolPrem = useCallback((data) => {
    const copyText = data?.copy_text_without_vol_prem;
    if (typeof copyText !== "string" || !copyText.length) return 0;
    copyToClipboard(copyText);
    return copyText.split("\n").length;
  }, []);

  const finishTeamRunPush = useCallback(
    async (summaryId) => {
      const response = await finishQuickAddTeamRunPush([summaryId]);
      if (isErrorResponse(response)) return;
      applyDraftResponse(response);
      const pushed = response?.data?.data?.pushed_summary_ids || [];
      if (pushed.length) logStoreSuccess(pushed);
    },
    [applyDraftResponse, logStoreSuccess]
  );

  const openNextTeamRunPopup = useCallback(async () => {
    const next = teamRunQueueRef.current.shift();
    if (!next) {
      setTeamRunPopup(null);
      setIsStoring(false);
      schedule(focusInput, 0);
      return;
    }
    try {
      // Same check as the Pricer button: quantity, expiry, already on Team
      // Run (update popup) or new (trader popup).
      const res = await triggerPushOptionToTeamRunAPI({
        summary_id: next.summaryId,
      });
      const popup = teamRunPopupFromPushCheck(res?.data?.data, next);
      if (!popup) {
        toast(
          res?.data?.message ||
            "Team Run did not ask for a confirmation popup.",
          "warning"
        );
        openNextTeamRunPopup();
        return;
      }
      setTeamRunPopup(popup);
    } catch {
      // The interceptor already showed the push-check validation message.
      openNextTeamRunPopup();
    }
  }, [focusInput, schedule]);

  const handleTeamRunPopupClose = useCallback(async () => {
    const closing = teamRunPopup;
    setTeamRunPopup(null);
    if (closing?.summaryId) await finishTeamRunPush(closing.summaryId);
    openNextTeamRunPopup();
  }, [teamRunPopup, finishTeamRunPush, openNextTeamRunPopup]);

  const handleSubmitToPricer = useCallback(async () => {
    const ids = draftIdsRef.current;
    if (!ids.length) {
      toast("Stage at least one option first", "warning");
      return;
    }
    setIsStoring(true);
    try {
      const response = await submitQuickAddDraftsToPricer(ids);
      if (isErrorResponse(response)) {
        toastImportValidationResponse(response);
        return;
      }
      const lineCount = copyNoVolPrem(response?.data?.data);
      applyDraftResponse(response);
      logStoreSuccess(ids);
      toast(
        lineCount > 1
          ? `Added to the Pricer and copied ${lineCount} no vol/prem shorthands`
          : lineCount === 1
            ? "Added to the Pricer and copied the no vol/prem shorthand"
            : "Added to the Pricer",
        "success"
      );
      sendEvent({
        category: CATEGORIES.PRICER,
        context: CONTEXTS.PRICER,
        action: ACTIONS.CLICK,
        component: COMPONENT_NAMES.BUTTON,
        eventName: EVENT_NAMES.PRICER_QUICK_ADD_SUCCESS,
        extraParams: {
          row_count: ids.length,
          destination: "pricer",
        },
      });
      if (refetchOptionRow) refetchOptionRow();
      schedule(focusInput, 0);
    } catch {
      toast("Couldn't submit to the Pricer. Please try again.", "error");
    } finally {
      setIsStoring(false);
    }
  }, [
    applyDraftResponse,
    copyNoVolPrem,
    focusInput,
    logStoreSuccess,
    refetchOptionRow,
    schedule,
  ]);

  const handlePushToTeamRun = useCallback(async () => {
    const ids = draftIdsRef.current;
    if (!ids.length) {
      toast("Stage at least one option first", "warning");
      return;
    }
    setIsStoring(true);
    try {
      const copyResponse = await fetchQuickAddCopyText(ids);
      const lineCount = isErrorResponse(copyResponse)
        ? 0
        : copyNoVolPrem(copyResponse?.data?.data);
      if (lineCount) {
        toast(
          lineCount > 1
            ? `Copied ${lineCount} no vol/prem shorthands. Confirm each Team Run popup.`
            : "Copied the no vol/prem shorthand. Confirm the Team Run popup.",
          "success"
        );
      }
      sendEvent({
        category: CATEGORIES.PRICER,
        context: CONTEXTS.PRICER,
        action: ACTIONS.CLICK,
        component: COMPONENT_NAMES.BUTTON,
        eventName: EVENT_NAMES.PRICER_QUICK_ADD_SUCCESS,
        extraParams: {
          row_count: ids.length,
          destination: "team_run",
        },
      });
      teamRunQueueRef.current = ids.map((summaryId) => ({
        summaryId,
        productId: draftProductIds.get(summaryId) ?? null,
      }));
      openNextTeamRunPopup();
    } catch {
      setIsStoring(false);
      toast("Couldn't start the Team Run push. Please try again.", "error");
    }
  }, [copyNoVolPrem, draftProductIds, openNextTeamRunPopup]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const target = e.target;
      // Ctrl/⌘+Enter stores from anywhere inside the panel (input or cells).
      if (e.key === "Enter" && target?.closest?.(".quick-add-panel")) {
        e.preventDefault();
        if (!isStoring && draftIdsRef.current.length) handleSubmitToPricer();
        return;
      }
      if (e.key.toLowerCase() !== "z") return;
      const inputEl = inputRef.current?.input || inputRef.current;
      if (target === inputEl) {
        e.preventDefault();
        handleUndoLast();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, handleUndoLast, handleSubmitToPricer, isStoring]);

  const chips = useMemo(() => {
    if (!parseResult || !debouncedInput.trim()) return [];
    const list = [];
    if (parseResult.contract) {
      list.push({ key: "commodity", label: parseResult.contract.commodity });
    }
    if (parseResult.tenors?.length) {
      const hasExpired = parseResult.tenors.some((t) =>
        isTenorExpired(t, {
          grammarPack,
          contractToken: parseResult.contract?.token,
        })
      );
      list.push({
        key: "tenor",
        label: hasExpired
          ? `${parseResult.tenors.join("/")} · expired?`
          : parseResult.tenors.join("/"),
        warn: hasExpired,
      });
    }
    if (parseResult.optionTypeName || parseResult.label) {
      list.push({
        key: "type",
        label: parseResult.optionTypeName || parseResult.label,
      });
    }
    if (parseResult.strikes?.length) {
      list.push({
        key: "strikes",
        label: `${parseResult.strikes.join(" / ")} · ${
          parseResult.legs?.map((l) => l.cp).join("/") || ""
        }`,
      });
    }
    if (parseResult.legs?.length) {
      list.push({
        key: "bs",
        label: parseResult.legs.map((l) => l.bs).join("/"),
      });
    }
    if (parseResult.live === 1) {
      list.push({ key: "live", label: "live" });
    } else if (parseResult.underlying != null) {
      list.push({
        key: "ul",
        label: `hedged · UL ${parseResult.underlying}`,
      });
    }
    // Missing parts → quiet live hint (not red chips — those shout while typing).
    return list;
  }, [parseResult, debouncedInput, grammarPack]);

  const liveMissingHint = useMemo(
    () => getLiveMissingHint(parseResult, debouncedInput),
    [parseResult, debouncedInput]
  );

  const panelTitle = (
    <span className="quick-add__title">
      <b>Quick Add</b>
      <button
        type="button"
        className="quick-add__help-btn"
        aria-label="How to use Quick Add"
        title="How to use"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          setHelpOpen(true);
        }}
      >
        <QuestionCircleOutlined />
      </button>
      <span className="quick-add__title-hint">
        drag · resize · ⌘/Ctrl+Enter submits · ? for the full guide
      </span>
    </span>
  );

  return (
    <>
      <FloatingPanel
        open={isOpen}
        onClose={() => onClose(false)}
        title={panelTitle}
        dark
        className="quick-add-panel"
        bodyClassName="quick-add"
        panelOptions={PANEL_OPTIONS}
        // The Team Run popups are antd Modals (z 1000); step below them, or
        // they and their nested Add Company modal open behind the panel.
        zIndex={teamRunPopup ? QUICK_ADD_PANEL_Z_BEHIND_MODAL : undefined}
      >
        <div
          className={classnames("quick-add__bar-wrap", {
            "quick-add__shake": shake,
          })}
        >
          <span className="quick-add__plus" aria-hidden>
            ＋
          </span>
          <div className="quick-add__input-wrap">
            {suggestion && !inputOverflow && (
              <div className="quick-add__ghost" aria-hidden>
                <span className="quick-add__ghost-typed">{inputValue}</span>
                <span className="quick-add__ghost-rest">
                  {suggestion.remainder}
                </span>
              </div>
            )}
            <Input
              ref={inputRef}
              className="quick-add__input"
              placeholder={
                grammarLoading
                  ? "Loading contracts and strategies…"
                  : grammarError
                    ? "Couldn't load contracts — reload the page"
                    : "e.g. EUA jun27 60/55cs x56"
              }
              value={inputValue}
              disabled={grammarLoading || grammarError}
              onChange={(e) => {
                setInputValue(e.target.value);
                if (stageWarning) setStageWarning("");
              }}
              onPressEnter={(e) => {
                // Ctrl/⌘+Enter is Store, handled by the panel-level listener.
                if (!e.ctrlKey && !e.metaKey) handleStage();
              }}
              onKeyDown={handleInputKeyDown}
              onPaste={handlePaste}
              allowClear
            />
          </div>
          <PrimaryButton
            className="primary-button quick-add__add-btn"
            size="small"
            disabled={grammarLoading || grammarError}
            loading={isStaging}
            onClick={handleStage}
          >
            Add
          </PrimaryButton>
          <span className="quick-add__enter-hint">Enter = Add ↵</span>
        </div>

        {stageWarning ? (
          <p className="quick-add__stage-warning" role="alert">
            {stageWarning}
          </p>
        ) : liveMissingHint ? (
          <p className="quick-add__live-hint" aria-live="polite">
            {liveMissingHint}
          </p>
        ) : null}

        {rowLevelWarnings.length ? (
          <ul className="quick-add__import-errors" role="alert">
            {rowLevelWarnings.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        ) : null}

        <div className="quick-add__anatomy" aria-label="Shorthand format">
          {SHORTHAND_ANATOMY.map(({ token, label }) => (
            <span key={label} className="quick-add__anatomy-part">
              <span className="quick-add__anatomy-token">{token}</span>
              <span className="quick-add__anatomy-label">{label}</span>
            </span>
          ))}
        </div>

        <p className="quick-add__rules">
          <span>Any order</span>
          <span className="quick-add__rules-sep">·</span>
          <span>
            one strike for a single leg (<code>100c</code>)
          </span>
          <span className="quick-add__rules-sep">·</span>
          <span>
            underlying required (<code>x56</code> or <code>live</code>)
          </span>
        </p>

        <div className="quick-add__chips">
          {chips.map((c) => (
            <span
              key={c.key}
              className={classnames("quick-add__chip", {
                "quick-add__chip--warn": c.warn,
                "quick-add__chip--error": c.error,
              })}
            >
              {c.label}
            </span>
          ))}
        </div>

        <div className="quick-add__table-wrap">
          {draftIds.length ? (
            <>
              <div className="quick-add__staged-toolbar">
                {draftIds.map((summaryId, index) => (
                  <span
                    key={summaryId}
                    title={draftShorthands?.[summaryId] || undefined}
                    className={classnames("quick-add__staged-chip", {
                      "quick-add__staged-chip--new": summaryId === newSummaryId,
                    })}
                  >
                    Option #{index + 1}
                    <span className="quick-add__row-actions">
                      <InsertRowBelowOutlined
                        className="quick-add__duplicate"
                        title="Duplicate option"
                        onClick={() => handleDuplicateDraft(summaryId)}
                      />
                      <CloseOutlined
                        className="quick-add__delete"
                        title="Remove option"
                        onClick={() => handleDeleteDraft(summaryId)}
                      />
                    </span>
                  </span>
                ))}
              </div>
              <QuickAddStagingGrid
                rows={draftRows}
                inputs={draftInputs}
                setRows={setDraftRowsFromGrid}
                reloadRows={reloadDrafts}
              />
            </>
          ) : (
            <p className="quick-add__empty-table">
              {isStaging
                ? "Pricing the option…"
                : "Nothing staged yet — type a shorthand and press Enter"}
            </p>
          )}
        </div>

        <div className="quick-add__footer">
          <span className="quick-add__footer-note">
            Tab accepts a suggestion · paste several lines to stage them all ·
            expand legs like the Pricer
          </span>
          <span className="quick-add__footer-actions">
            <PrimaryButton
              className="primary-button quick-add__store"
              loading={isStoring}
              disabled={!draftIds.length || isStoring || isStaging}
              onClick={handlePushToTeamRun}
            >
              Push to Team Run and Copy - no vol/prem
            </PrimaryButton>
            <PrimaryButton
              className="primary-button quick-add__store"
              loading={isStoring}
              disabled={!draftIds.length || isStoring || isStaging}
              onClick={handleSubmitToPricer}
            >
              Submit to Pricer and Copy - no vol/prem
            </PrimaryButton>
          </span>
        </div>
      </FloatingPanel>

      <QuickAddHelp
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        dark
      />

      <QuickAddTeamRunPopup
        popup={teamRunPopup}
        onClose={handleTeamRunPopupClose}
      />
    </>
  );
};

export default QuickAdd;
